import { type FC } from "react";
import { Form, Field } from "react-final-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  TextField,
} from "@mui/material";
import { getLuminance } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { DialogHeader } from "@/shared/components";
import { ColorPicker } from "@/shared/components/ColorPicker";
import type { WidgetFormValues } from "@/partner/entities/widgets";

const DEFAULT_COLOR = "#e91e63";

interface WidgetFormProps {
  title: string;
  loading?: boolean;
  initialValues?: WidgetFormValues;
  onSubmit: (values: WidgetFormValues) => Promise<void>;
  onClose: () => void;
}

export const WidgetForm: FC<WidgetFormProps> = ({
  title,
  initialValues = {
    title: "",
    buttonUrl: "",
    buttonText: "",
    buttonColor: DEFAULT_COLOR,
  },
  loading = false,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Form<WidgetFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, values = initialValues }) => {
        const buttonColor = values.buttonColor || DEFAULT_COLOR;
        const buttonTextColor =
          getLuminance(buttonColor) > 0.5 ? "#000000" : "#ffffff";

        return (
          <form onSubmit={handleSubmit}>
            <DialogHeader
              title={title}
              actions={
                <Button variant="contained" type="submit" disabled={loading}>
                  {t("common.actions.save")}
                </Button>
              }
              onClose={onClose}
              loading={loading}
            />

            <Box sx={{ p: 1 }}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Card elevation={0}>
                    <CardContent sx={{ pt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <Field name="title">
                            {({ input }) => (
                              <TextField
                                {...input}
                                autoFocus
                                required
                                label={t("widgets.form.titleLabel")}
                                fullWidth
                                variant="outlined"
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid size={12}>
                          <Field name="buttonColor">
                            {({ input }) => (
                              <ColorPicker
                                label={t("widgets.form.buttonColorLabel")}
                                fullWidth
                                value={input.value || DEFAULT_COLOR}
                                onChange={input.onChange}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid size={12}>
                          <Field name="buttonText">
                            {({ input }) => (
                              <TextField
                                {...input}
                                required
                                label={t("widgets.form.buttonTextLabel")}
                                fullWidth
                                variant="outlined"
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid size={12}>
                          <Field name="buttonUrl">
                            {({ input }) => (
                              <TextField
                                {...input}
                                required
                                type="url"
                                label={t("widgets.form.buttonUrlLabel")}
                                fullWidth
                                variant="outlined"
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                      p: { xs: 5, sm: 6.25 },
                      textAlign: "center",
                    }}
                  >
                    <Button
                      disableElevation
                      variant="contained"
                      size="large"
                      style={{
                        backgroundColor: buttonColor,
                        color: buttonTextColor,
                      }}
                      onClick={(event) => event.preventDefault()}
                    >
                      {values.buttonText || t("widgets.form.example")}
                    </Button>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </form>
        );
      }}
    />
  );
};
